import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ResearchPortalBdService } from '../../../services/research-portal-bd.service';
import { MaterialPageGrid, MaterialListQuery } from '../../../utils/material-page-grid';
import { interval, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DatePipe } from '@angular/common';

export interface MarketInsight {
  stock?: string;
  insight?: string;
  topic?: string;
  alertType?: string;
  created_at?: string;
  timestamp_string?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-market-insights',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    ReactiveFormsModule,
  ],
  providers: [DatePipe],
  templateUrl: './market-insights.component.html',
  styleUrl: './market-insights.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MarketInsightsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  // @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = ['rowNo', 'stock', 'insight', 'topic', 'alertType', 'timestamp_string'];
  // marketInsightsPageGrid: MaterialPageGrid<MarketInsight>;
  marketInsights: any = [];
  marketInsightsMaster: any = [];
  gridInProgress = false;
  pageSizes = [10, 15, 20, 25, 50, 100];
  countdownSeconds = 5 * 60; // 5 minutes in seconds
  countdownDisplay = '05:00';
  alertTypeOptions = [
    { value: '', label: 'All' },
    { value: 'MARKET_STAT', label: 'Market' },
    { value: 'ANNOUNCEMENT', label: 'News' },
    { value: 'STOCK_MOVE', label: 'Stock' },
  ];

  langTypes = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'বাংলা' },
  ];
  langController = new FormControl('en');
  alertTypeController = new FormControl('');
  stockCodeController = new FormControl('');
  private refreshSubscription: Subscription;
  private countdownSubscription: Subscription;
  private readonly REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
  private readonly REFRESH_INTERVAL_SECONDS = 5 * 60; // 5 minutes in seconds

  constructor(
    private researchPortalBdService: ResearchPortalBdService,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  async ngOnInit() {
    await this.initGrid();
    this.startAutoRefresh();
    this.startCountdown();

    this.alertTypeController.valueChanges.subscribe((value) => {
      this.stockCodeController.setValue('', { emitEvent: false });
      if (value) {
        this.marketInsights = this.marketInsightsMaster.filter((item) => item.alert_type === value);
        this.cdRef.detectChanges();
      } else {
        this.marketInsights = this.marketInsightsMaster;
      }
      this.cdRef.detectChanges();
    });
    this.stockCodeController.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      if (value) {
        this.marketInsights = this.marketInsightsMaster.filter((item) => item.stock_code?.toLowerCase().includes(value.toLowerCase()));
      } else {
        this.marketInsights = this.marketInsightsMaster;
      }
      this.cdRef.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    // setTimeout(() => {
    //   if (this.marketInsightsPageGrid && this.sort) {
    //     this.marketInsightsPageGrid.attachSort(this.sort);
    //   }
    // }, 0);
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    this.stopCountdown();
  }

  async initGrid(): Promise<void> {
    try {
      this.marketInsightsMaster = [];
      this.marketInsights = [];
      this.gridInProgress = true;
      this.cdRef.detectChanges();
      let allData: any = [];
      allData = await this.researchPortalBdService.getMarketInsights();
      let sortedData = [...(allData || [])];
      if (sortedData && sortedData.length > 0) {
        sortedData = sortedData.map((item) => ({
          ...item,
          timestamp_string: item.created_at ? this.datePipe.transform(item.created_at, 'yyyy/MM/dd HH:mm:ss') : '-',
        }));
      }

      if (sortedData.length > 0) {
        sortedData.sort((a, b) => {
          // Default: sort by created_at descending (newest first)
          // This is the primary sort when no column is selected
          const timestampA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const timestampB = b.created_at ? new Date(b.created_at).getTime() : 0;

          // Default: newest first (descending by created_at)
          return timestampB - timestampA;
        });
      }
      this.marketInsightsMaster = [...(sortedData || [])];
      this.marketInsights = sortedData;
      this.gridInProgress = false;
      this.cdRef.detectChanges();
    } catch (e) {
      this.gridInProgress = false;
      this.cdRef.detectChanges();
    }

    // this.marketInsightsPageGrid = new MaterialPageGrid<MarketInsight>(this.fb, 15)
    //   .setTableColumns(this.displayedColumns)
    //   .setSource(async (listQuery: MaterialListQuery) => {
    //     try {
    //       this.gridInProgress = true;
    //       this.cdRef.detectChanges();
    //
    //       // Fetch all market insights data
    //       let allData: any = [];
    //       if(!listQuery.filterValues?.stockCode && listQuery.filterValues?.languageChange) {
    //         allData =   await this.researchPortalBdService.getMarketInsights();
    //       }
    //       // Sort data
    //       let sortedData = [...(allData || [])];
    //
    //       sortedData = sortedData.map((item) => ({
    //         ...item,
    //         timestamp_string: item.created_at ? new Date(item.created_at).toLocaleString() : '-',
    //       }));
    //
    //       // Apply Alert Type filter
    //       const alertTypeFilter = listQuery.filterValues?.alertType;
    //       if (alertTypeFilter && alertTypeFilter !== '') {
    //         sortedData = sortedData.filter((item) => item.alert_type === alertTypeFilter);
    //       }
    //
    //       if (sortedData.length > 0) {
    //         sortedData.sort((a, b) => {
    //           // Default: sort by created_at descending (newest first)
    //           // This is the primary sort when no column is selected
    //           const timestampA = a.created_at ? new Date(a.created_at).getTime() : 0;
    //           const timestampB = b.created_at ? new Date(b.created_at).getTime() : 0;
    //
    //           // If explicitly sorting by created_at
    //           // if (listQuery.sortActive === 'timestamp' && listQuery.sortDirection) {
    //           //   return listQuery.sortDirection === 'asc' ? timestampA - timestampB : timestampB - timestampA;
    //           // }
    //
    //           // Default: newest first (descending by created_at)
    //           return timestampB - timestampA;
    //         });
    //       }
    //
    //       // Apply pagination
    //       // const start = listQuery.pageIndex * listQuery.pageSize;
    //       // const end = start + listQuery.pageSize;
    //       // const paginatedData = sortedData.slice(start, end);
    //
    //       this.gridInProgress = false;
    //       this.cdRef.detectChanges();
    //
    //       return { data: sortedData, total: sortedData.length };
    //     } catch (error) {
    //       console.error('Error loading market insights:', error);
    //       this.gridInProgress = false;
    //       this.cdRef.detectChanges();
    //       return { data: [], total: 0 };
    //     }
    //   })
    //   .setFilters({
    //     alertType: '',
    //     stockCode: ''
    //   });

    // Initial data load
    // await this.marketInsightsPageGrid.refreshData();
    this.cdRef.detectChanges();
  }

  startAutoRefresh(): void {
    console.log('startAutoRefresh log');
    this.refreshSubscription = interval(this.REFRESH_INTERVAL).subscribe(() => {
      console.log('startAutoRefresh');
      this.refreshData();
      this.resetCountdown();
    });
  }

  stopAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  startCountdown(): void {
    this.countdownSubscription = interval(1000).subscribe(() => {
      if (this.countdownSeconds > 0) {
        this.countdownSeconds--;
        this.updateCountdownDisplay();
      } else {
        this.resetCountdown();
      }
    });
  }

  stopCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
  }

  resetCountdown(): void {
    this.countdownSeconds = this.REFRESH_INTERVAL_SECONDS;
    this.updateCountdownDisplay();
  }

  updateCountdownDisplay(): void {
    const minutes = Math.floor(this.countdownSeconds / 60);
    const seconds = this.countdownSeconds % 60;
    this.countdownDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    this.cdRef.detectChanges();
  }

  async refreshData(): Promise<void> {
    console.log('refreshData');
    await this.initGrid();
    this.stopAutoRefresh();
    this.startAutoRefresh();
    this.resetCountdown();
    this.cdRef.detectChanges();
  }

  onManualRefresh(): void {
    this.refreshData();
  }
}
